// What counts as a picture, for every surface that shows one.
//
// This lived in src/components/Attachments.jsx, which imports StorageManager
// and @aws-amplify/ui-react/styles.css at module scope -- so every reader of
// this six-line rule dragged the whole Amplify UI storage widget in with it.
// The preview Lambda needs the same rule and cannot import React at all, which
// is what finally moved it here.

import {
  attachmentPreviewKey,
  isAttachmentPreviewKey,
  urlForLogbookEntryStorage,
  urlForTaskStorage,
} from './Urls.mjs';

// The per-file upload ceiling, spelled once for both uploaders (the
// attachments card and an item's profile photo). It lives beside canPreview
// because it is the same question from the other end: how big a file the app
// accepts decides what attachment-preview is asked to decode. Here rather than
// in components/Attachments.jsx for this file's whole reason -- a caller that
// only wants the number should not drag the Amplify UI storage widget in.
//
// It is a SIZE limit and says nothing about how MANY files one upload carries,
// which is unbounded. The two used to be two spellings of "10" a line apart.
//
// A photo at this size may be more than attachment-preview can decode (it
// buffers the original and runs sharp at 1024 MB / 60 s). That degrades
// correctly and needs no Lambda change: a preview is an optimisation, never a
// dependency, so a photo without one falls back to the original -- and
// anything canPreview rejects never reaches sharp at all.
export const MAX_UPLOAD_FILE_SIZE = 50 * 1024 * 1024;

// ---- What a VISITOR may upload to a shared trip -----------------------------
//
// Deliberately smaller numbers than the owner's, and the difference is the whole
// point: MAX_UPLOAD_FILE_SIZE is about what an owner may store in their own
// bucket, and these are about what a STRANGER holding a share link may cost
// them. 60 x 15 MB is roughly 900 MB, which is the worst case a single leaked
// link can inflict; at the owner's 50 MB ceiling the same caps would be 3 GB.
//
// Here rather than in the Lambda for this file's usual reason: both runtimes
// need them. TaskPogoShareUpload enforces them, and the browser pre-checks a
// picked file against the same numbers so an obviously-too-big photo is named
// without a round trip.
export const MAX_GUEST_UPLOAD_FILE_SIZE = 15 * 1024 * 1024;
export const MAX_GUEST_UPLOAD_FILES = 10;
export const MAX_GUEST_PHOTOS_PER_ENTRY = 24;
export const MAX_GUEST_PHOTOS_PER_TRIP = 60;

// The same two caps for a shared TASK list (TaskPogoShareTasksWrite). Separate
// constants rather than the entry pair reused under a second name: the two
// features have separate off switches and separate blast radii, and the day one
// of them wants a different number, sharing the constant would move both.
//
// The "list" cap is counted over the tasks THIS SHARE covers -- the tasks
// currently assigned to one name on one item -- not over the item's whole
// `<itemId>/task/` prefix, which spans tasks the link cannot reach. Counting
// the prefix would let an unrelated task's photos lock a visitor out.
export const MAX_GUEST_PHOTOS_PER_TASK = 24;
export const MAX_GUEST_PHOTOS_PER_TASK_LIST = 60;

// ---- Naming a visitor's file ------------------------------------------------
//
// A visitor's photo goes into the entry's ordinary folder, beside the owner's,
// so everything that already displays a photo displays this one too. The name is
// what keeps that safe, and it carries four separate loads:
//
//   - EXACTLY ONE PATH SEGMENT. groupPhotosByEntry only accepts keys shaped
//     `<entryId>/<name>`, so a name containing a slash would be both a traversal
//     attempt and an invisible photo.
//   - NEVER STARTS WITH `~~`. isAttachmentPreviewKey reads the FILE NAME, so a
//     `~~preview-` spelling would make attachment-preview skip the upload and
//     make it a preview of nothing.
//   - STILL AN IMAGE NAME. isImage has to stay true of it or the photo is stored
//     and then never drawn.
//   - COLLISION-PROOF against the owner's own files. A presigned PUT overwrites
//     silently, and a visitor overwriting the owner's photo of record is the
//     worst outcome available here -- hence the random token, not just a
//     timestamp.
//
// `guest-` is also what the per-entry and per-trip caps count, and what the
// shared strips and the owner's Attached Files card badge.
export const GUEST_PHOTO_PREFIX = 'guest-';

const MAX_GUEST_BASE_NAME = 40;

export function isGuestPhotoName(name) {
  if (typeof name !== 'string') return false;
  return name.startsWith(GUEST_PHOTO_PREFIX);
}

// `guest-<base36 minute>-<token>-<safe base>.<ext>`.
//
// `ext` is taken from the ORIGINAL name and must already have been validated by
// the caller -- this function is about shape, not about which formats are
// allowed (that is canPreview's job, and the upload endpoint's).
export function guestPhotoName(originalName, token) {
  const name = typeof originalName === 'string' ? originalName : '';
  const dot = name.lastIndexOf('.');
  const rawBase = dot > 0 ? name.slice(0, dot) : name;
  const rawExt = dot > 0 ? name.slice(dot + 1) : '';

  const base = rawBase
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, MAX_GUEST_BASE_NAME)
    // A name that was nothing but separators slices down to '' or to a stray
    // dash; either way there is no base left to keep.
    .replace(/^-|-$/g, '');

  const ext = rawExt.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const stamp = Math.floor(Date.now() / 60000).toString(36);
  const safeToken = String(token ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  return `${GUEST_PHOTO_PREFIX}${stamp}-${safeToken}-${base || 'photo'}${ext ? `.${ext}` : ''}`;
}

// The absolute S3 key one visitor photo will live at.
//
// `entryId` is the only segment a caller supplies, and the upload endpoint must
// already have proved it belongs to the share's trip -- everything else is derived
// from an owner-authed share row. Built from urlForLogbookEntryStorage rather than
// by hand so a visitor's photo lands in exactly the folder the owner's own
// uploader writes to, which is what makes every existing display path pick it up
// for free.
export function guestPhotoKey({ identityId, itemId, entryId, fileName, token }) {
  const name = guestPhotoName(fileName, token);
  return `private/${identityId}/${urlForLogbookEntryStorage(itemId, entryId)}${name}`;
}

// The same, for one TASK's folder. Built from urlForTaskStorage for exactly the
// reason above: the file lands in the folder Task.jsx's Attached Files card
// already reads, so the owner sees a visitor's photo on the task page with no
// display code added anywhere. `taskId` is the only caller-supplied segment and
// the endpoint must already have proved it belongs to the share.
export function guestTaskPhotoKey({ identityId, itemId, taskId, fileName, token }) {
  const name = guestPhotoName(fileName, token);
  return `private/${identityId}/${urlForTaskStorage(itemId, taskId)}${name}`;
}

// ---- What a visitor may upload ----------------------------------------------
//
// Here rather than in the Lambda because BOTH runtimes need the same answer: the
// endpoint enforces it, and the browser pre-checks a picked file against it so an
// obviously-bad one is named without a round trip. The doubled whitelist, the same
// shape as the field whitelist on the read side.

// The content type S3 will store, DERIVED FROM THE EXTENSION and never taken from
// the client. The type is signed into the presigned PUT, so the browser must send
// this exact value and S3 stores exactly it -- which is what puts `text/html` out
// of reach at an origin that also serves every other presigned photo on the page.
const GUEST_CONTENT_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
};

export function guestContentType(name) {
  return GUEST_CONTENT_TYPES[String(name ?? '').split('.').pop()?.toLowerCase()] ?? null;
}

/**
 * Why a file may not be added, or null.
 *
 * THE ALLOWLIST IS canPreview's SET, NOT isImage's. Three independent reasons,
 * and the first is the one that would bite:
 *
 *   1. isImage admits SVG. The photo viewer's Download link and the zip both hand
 *      a visitor a URL at the S3 REST origin -- the same origin serving every
 *      other presigned photo on that page -- so an SVG uploaded by one visitor and
 *      opened by the next is stored XSS there. Not the app's origin, so no app
 *      session is at risk, but a script running there can read the objects it is
 *      handed. Excluding SVG costs nothing real.
 *   2. Only canPreview formats get a `~~preview-` (attachment-preview's own
 *      skipReason). Accepting GIF or BMP would put multi-megabyte originals into
 *      thumbnail strips forever.
 *   3. HEIC is already outside isImage, correctly -- browsers cannot decode it --
 *      but someone picking one off an iPhone deserves to be TOLD rather than to
 *      have it silently dropped.
 */
export function guestUploadRejection(file, maxBytes = MAX_GUEST_UPLOAD_FILE_SIZE) {
  const name = typeof file?.name === 'string' ? file.name : '';
  if (!name) return 'That file has no name.';

  if (!canPreview(name) || !guestContentType(name)) {
    return 'Only JPEG, PNG, WebP and AVIF photos can be added. (iPhone HEIC photos need converting first.)';
  }

  const size = Number(file?.size);
  if (!Number.isFinite(size) || size <= 0) {
    // groupPhotosByEntry drops sizeless objects, so one would upload and then
    // never appear -- worse than a refusal.
    return 'That file looks empty.';
  }
  if (size > maxBytes) {
    return `That photo is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  }

  return null;
}

// The name to save a photo under, with a visitor's bookkeeping taken back off.
// `guest-m1h2-9f3a1c-img-0421.jpg` is the right key and the wrong filename.
export function displayPhotoName(name) {
  if (!isGuestPhotoName(name)) return name;
  // Drop the prefix and the two generated segments, keeping whatever the
  // visitor's file was actually called. A name that doesn't have all three
  // segments isn't one we minted, so it comes back untouched.
  const rest = name.slice(GUEST_PHOTO_PREFIX.length);
  const parts = rest.split('-');
  if (parts.length < 3) return name;
  return parts.slice(2).join('-');
}

// Extensions we render inline as an <img>. Only formats a browser can actually
// decode in an <img> tag belong here -- anything else falls through to the
// generic file/download row. Notably HEIC/HEIF (iPhone photos) and TIFF are
// intentionally excluded: browsers can't render them, so they'd show a broken
// image rather than a clean download link.
const imageExtensions = new Set(['jpg', 'jpeg', 'jfif', 'png', 'gif', 'bmp', 'webp', 'avif', 'svg']);

export function isImage(filename) {
  if (!filename || !filename.includes('.')) {
    return false;
  }
  let extension = filename.split('.').slice(-1)[0].toLowerCase();
  return imageExtensions.has(extension);
}

// The formats sharp can actually re-encode. A subset of isImage: SVG is a
// vector the browser draws from markup (rasterising it server-side would be a
// different picture, and a hostile one is a parser exploit), and a GIF's first
// frame is not the GIF. Both are shown as-is, at whatever size they are.
const previewableExtensions = new Set(['jpg', 'jpeg', 'jfif', 'png', 'webp', 'avif']);

export function canPreview(filename) {
  if (!isImage(filename)) {
    return false;
  }
  return previewableExtensions.has(filename.split('.').slice(-1)[0].toLowerCase());
}

// ---- Pairing previews to the photos they belong to -------------------------
//
// A preview sits in the same folder as its photo, so one S3 listing carries
// both. Pairing off the LISTING rather than guessing a key and letting a 404
// decide is what makes a missing preview free: a photo the Lambda hasn't
// reached, or choked on, simply has none and the caller falls back to the
// original. Shared by the Attached Files card and the trip page's photo strip
// so there is one answer to "which file do I actually download".

// Split a flat listing into the previews (indexed by their own key) and
// everything else. `items` are S3 listing entries, `{ key, size, ... }`.
export function splitPreviews(items) {
  const previews = new Map();
  const rest = [];
  for (const item of items) {
    if (isAttachmentPreviewKey(item.key)) {
      previews.set(item.key, item);
    } else {
      rest.push(item);
    }
  }
  return { previews, rest };
}

// The preview belonging to one listing entry, or null.
export function previewFor(item, previews) {
  return previews.get(attachmentPreviewKey(item.key)) ?? null;
}

// ---- Grouping one logbook's listing into photos per entry ------------------
//
// Attachments are keyed `${itemId}/logbook/${entryId}/<file>`
// (Urls.urlForLogbookEntryStorage), so one listing of `${itemId}/logbook/`
// carries every entry's photos and the grouping is a path segment.
//
// This lived in src/pages/logbook/useEntryPhotos.js, which imports Database.mjs
// and so aws-amplify. It moved here for the reason the file's header already
// gives for isImage: TaskPogoShareLogbookTrip lists the same prefix out of S3
// to presign a shared trip's photos, and a Lambda cannot import React or the
// Amplify client. Two copies of this would be free to disagree about which
// files are photos and what order they come in -- which, since the caller keeps
// only the first few, decides WHICH photos a reader sees.
//
// Pure: `items` are `{ key, size }` listing entries and `root` is the prefix
// they were listed under. The browser lists relative to its `private/<id>/`
// access level and the Lambda lists absolute keys, so `root` differs between
// the two callers and nothing here may assume either spelling.

export function groupPhotosByEntry(items, root) {
  const { previews, rest } = splitPreviews(items);
  const byEntry = new Map();

  for (const item of rest) {
    // A folder marker carries no bytes; processStorageList drops these too.
    if (!item.size) continue;
    if (!item.key.startsWith(root)) continue;

    // `<entryId>/<name>` exactly. Anything deeper is a sub-folder file, which
    // the Attached Files card doesn't show either.
    const parts = item.key.slice(root.length).split('/');
    if (parts.length !== 2) continue;

    const [entryId, name] = parts;
    if (!entryId || !name) continue;
    if (!isImage(name)) continue;

    const list = byEntry.get(entryId) ?? [];
    list.push({ key: item.key, name, size: item.size, preview: previewFor(item, previews) });
    byEntry.set(entryId, list);
  }

  // Numeric-aware, because S3 hands back lexicographic order and the cap keeps
  // the first few: plain sorting puts IMG_10 before IMG_2, so "the first six"
  // would not be the six a reader means. lastModified is no better -- a batch
  // upload stamps them within a second of each other.
  for (const list of byEntry.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }

  return byEntry;
}
